import type { BadgeTone } from '@fas/ui';
import type {
  ActualModality,
  AttendanceFlagType,
  AttendanceStatus,
  ClockInStatus,
  ClockOutStatus,
  DailyStatus,
  DeviceCheckStatus,
  LiveStatus,
  LocationStatus,
  UserDeviceStatus,
  WorkArrangement,
} from '@fas/types';

type Label = { label: string; tone: BadgeTone };

/** §76 rule 20: pending, verified, approved and payroll-ready are always distinguishable. */
export const ATTENDANCE_STATUS: Record<AttendanceStatus, Label> = {
  SCHEDULED: { label: 'Not yet clocked in', tone: 'neutral' },
  CLOCKED_IN: { label: 'Clocked in', tone: 'info' },
  CLOCKED_OUT: { label: 'Clocked out', tone: 'info' },
  MISSING_CLOCK_OUT: { label: 'Missing clock-out', tone: 'danger' },
  PENDING_VERIFICATION: { label: 'Pending verification', tone: 'warning' },
  FLAGGED: { label: 'Needs review', tone: 'warning' },
  UNDER_REVIEW: { label: 'Under review', tone: 'warning' },
  VERIFIED: { label: 'Verified', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  APPROVED: { label: 'Approved', tone: 'success' },
  PAYROLL_READY: { label: 'Payroll-ready', tone: 'success' },
  PROCESSED: { label: 'Processed', tone: 'neutral' },
  LOCKED: { label: 'Locked', tone: 'neutral' },
  CORRECTION_REQUESTED: { label: 'Correction requested', tone: 'warning' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  CLASS_SUSPENDED: { label: 'Class suspended', tone: 'neutral' },
  ABSENT: { label: 'Absent', tone: 'danger' },
  NO_SHOW: { label: 'No-show', tone: 'danger' },
};

export const TIME_STATUS: Record<ClockInStatus, Label> = {
  EARLY: { label: 'Early', tone: 'success' },
  ON_TIME: { label: 'On time', tone: 'success' },
  LATE: { label: 'Late', tone: 'warning' },
  VERY_LATE: { label: 'Very late', tone: 'danger' },
  MISSING: { label: 'No clock-in', tone: 'danger' },
  OUTSIDE_ALLOWED_WINDOW: { label: 'Outside window', tone: 'danger' },
};

export const CLOCK_OUT_STATUS: Record<ClockOutStatus, Label> = {
  COMPLETE: { label: 'Complete', tone: 'success' },
  EARLY_CLOCK_OUT: { label: 'Left early', tone: 'warning' },
  UNDERTIME: { label: 'Undertime', tone: 'warning' },
  LATE_CLOCK_OUT: { label: 'Late clock-out', tone: 'warning' },
  MISSING_CLOCK_OUT: { label: 'Missing clock-out', tone: 'danger' },
};

export const LOCATION_STATUS: Record<LocationStatus, Label> = {
  WITHIN_GEOFENCE: { label: 'Location verified', tone: 'success' },
  OUTSIDE_GEOFENCE: { label: 'Outside geofence', tone: 'danger' },
  GPS_UNAVAILABLE: { label: 'No GPS reading', tone: 'warning' },
  GPS_PERMISSION_DENIED: { label: 'Location denied', tone: 'warning' },
  LOW_ACCURACY: { label: 'Low GPS accuracy', tone: 'warning' },
  APPROVED_REMOTE_LOCATION: { label: 'Approved remote location', tone: 'info' },
  LOCATION_REVIEW_REQUIRED: { label: 'Location needs review', tone: 'warning' },
  NOT_REQUIRED: { label: 'No location needed', tone: 'neutral' },
  VERIFIED_BY_QR: { label: 'Verified by room code', tone: 'success' },
  VERIFIED_BY_CARD_READER: { label: 'Verified by card reader', tone: 'success' },
  VERIFIED_BY_ROOM_TAG: { label: 'Verified by room tag', tone: 'success' },
  VERIFIED_BY_TIME_CLOCK: { label: 'Verified by time clock', tone: 'success' },
};

export const ACTUAL_MODALITY: Record<ActualModality, string> = {
  ONSITE: 'On-site',
  ONLINE: 'Online',
  HYBRID_COMBINATION: 'Hybrid (on-site and online)',
  REMOTE_APPROVED: 'Approved remote location',
  FIELD_CLINICAL: 'Field / clinical site',
};

export const WORK_ARRANGEMENT: Record<WorkArrangement, string> = {
  ONSITE: 'On campus',
  WORK_FROM_HOME: 'Work from home',
  OFFICIAL_BUSINESS: 'Official business',
};

export const DAILY_STATUS: Record<DailyStatus, Label> = {
  PRESENT: { label: 'Present', tone: 'success' },
  ABSENT: { label: 'Absent', tone: 'danger' },
  LATE: { label: 'Late', tone: 'warning' },
  UNDERTIME: { label: 'Undertime', tone: 'warning' },
  ON_LEAVE: { label: 'On leave', tone: 'info' },
  OFFICIAL_BUSINESS: { label: 'Official business', tone: 'info' },
  WORK_FROM_HOME: { label: 'Work from home', tone: 'info' },
  HOLIDAY: { label: 'Holiday', tone: 'neutral' },
  WORK_SUSPENDED: { label: 'Work suspended', tone: 'neutral' },
  REST_DAY: { label: 'Rest day', tone: 'neutral' },
  PARTIAL: { label: 'Partial', tone: 'warning' },
};

export const LIVE_STATUS_TONE: Record<LiveStatus, BadgeTone> = {
  NOT_STARTED: 'neutral',
  NOT_CLOCKED_IN: 'warning',
  ON_TIME: 'success',
  LATE: 'warning',
  TEACHING: 'info',
  ONLINE: 'info',
  NEEDS_REVIEW: 'warning',
  COMPLETED: 'success',
  ABSENT: 'danger',
  MISSING_CLOCK_OUT: 'danger',
  CANCELLED: 'neutral',
};

export const FLAG_LABEL: Record<AttendanceFlagType, string> = {
  LATE: 'Late',
  VERY_LATE: 'Very late',
  EARLY_CLOCK_OUT: 'Left early',
  UNDERTIME: 'Undertime',
  LATE_CLOCK_OUT: 'Late clock-out',
  MISSING_CLOCK_IN: 'Missing clock-in',
  MISSING_CLOCK_OUT: 'Missing clock-out',
  OUTSIDE_ALLOWED_WINDOW: 'Outside allowed window',
  OUTSIDE_GEOFENCE: 'Outside geofence',
  LOW_GPS_ACCURACY: 'Low GPS accuracy',
  GPS_UNAVAILABLE: 'GPS unavailable',
  GPS_PERMISSION_DENIED: 'Location permission denied',
  SUSPECTED_GPS_SPOOFING: 'Suspected GPS spoofing',
  IMPOSSIBLE_TRAVEL: 'Impossible travel',
  BELOW_MINIMUM_DURATION: 'Below minimum duration',
  EXCEEDS_MAXIMUM_DURATION: 'Exceeds maximum duration',
  EXCESS_MINUTES: 'Excess minutes',
  CONCURRENT_SESSION: 'Concurrent session',
  PROOF_REQUIRED: 'Proof required',
  MODALITY_MISMATCH: 'Modality mismatch',
  OFFLINE_CAPTURE: 'Offline capture',
  EXCESSIVE_CORRECTIONS: 'Excessive corrections',
  UNAPPROVED_REMOTE_WORK: 'Remote work needs approval',
  LOCATION_NOT_CONFIGURED: 'Location not configured',
  DUPLICATE_CREDIT: 'Possible duplicate credit',
  UNREGISTERED_DEVICE: 'Unregistered device',
  SHARED_DEVICE: 'Shared device',
  QR_LOCATION_MISMATCH: 'Room code does not match',
  QR_SCAN_MISSING: 'Room code not scanned',
  USER_NOT_VERIFIED: 'Not confirmed on the device',
  TIME_CLOCK_PUNCH: 'Time clock punch',
  MEETING_ATTENDANCE_SHORT: 'Short time in the online meeting',
  OTHER: 'Other',
};

/** How a clock event or check-in was captured. */
export const METHOD_LABEL: Record<string, string> = {
  MOBILE_GPS: 'Phone',
  WEB_BROWSER: 'Browser',
  QR_CODE: 'Room code',
  CAMPUS_KIOSK: 'Campus kiosk',
  MANUAL_APPROVED: 'Recorded manually',
  API_INTEGRATION: 'Integration',
  RFID: 'ID card at a kiosk',
  NFC_TAG: 'Room tag',
  BIOMETRIC: 'Biometric time clock',
};

/** Device check of a clock event (Phase 2B). */
export const DEVICE_CHECK: Record<DeviceCheckStatus, Label> = {
  REGISTERED: { label: 'Registered device', tone: 'success' },
  PENDING_APPROVAL: { label: 'Device awaiting approval', tone: 'warning' },
  REVOKED: { label: 'Revoked device', tone: 'danger' },
  UNREGISTERED: { label: 'Unregistered device', tone: 'warning' },
  NO_PROOF: { label: 'Device not identified', tone: 'neutral' },
  INVALID_PROOF: { label: 'Device check failed', tone: 'danger' },
};

/** Registration of a device (Phase 2B). */
export const USER_DEVICE_STATUS: Record<UserDeviceStatus, Label> = {
  PENDING_APPROVAL: { label: 'Awaiting approval', tone: 'warning' },
  ACTIVE: { label: 'Active', tone: 'success' },
  REVOKED: { label: 'Removed', tone: 'neutral' },
};

/** 1590 → "26.5 h". */
export function hours(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  const h = minutes / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
}

/** "8:02 AM" in the institution time zone. */
export function clockTime(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-PH', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

/** "8:00 AM – 10:00 AM" from two instants. */
export function clockRange(startIso: string, endIso: string, timeZone: string): string {
  return `${clockTime(startIso, timeZone)} – ${clockTime(endIso, timeZone)}`;
}

export function minutesText(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  return `${minutes} min`;
}
